import { StepperStep } from 'components/stepper/Stepper';
import { CapacityPlanRunStatus } from 'core/graphql/types';
import { formatDateTime } from 'core/utils/DateUtils';

/** The single next thing the supervisor can do with the Run. */
export type CapacityPlanNextAction =
  | 'compute'
  | 'compute-again'
  | 'computing'
  | 'queued'
  | 'none';

export interface RunProgress {
  steps: StepperStep[];
  nextAction: CapacityPlanNextAction;
  /** Why `nextAction` is 'none'; shown next to the disabled button. */
  blockedReason?: string;
  /** "12 rows without coordinates will be excluded", when any row lacks a pin. */
  exclusionHint?: string;
  /** "3 / 8 solves done" while the Run is Queued or Computing. */
  solveProgressLabel?: string;
}

/** The counters `deriveRunProgress` reads off a Run; counts only, no rows. */
export interface RunProgressRun {
  status: CapacityPlanRunStatus;
  totalDeliveries: number;
  /** Deliveries that have coordinates. */
  geocodedDeliveries: number;
  validDeliveries: number;
  pendingGeocodes: number;
  failedGeocodes: number;
  missingCoordinates: number;
  computedAt?: string | null;
  solveProgress: {
    total: number;
    queued: number;
    computing: number;
    computed: number;
    failed: number;
  };
}

/** A Run whose Solves are on the compute queue or on a Worker. */
export const isRunSolving = (status: CapacityPlanRunStatus) =>
  status === CapacityPlanRunStatus.Queued ||
  status === CapacityPlanRunStatus.Computing;

/** "3 / 8 solves done", or the same line plus how many already failed. */
export const solveProgressLabelOf = (run: RunProgressRun): string => {
  const done = `${run.solveProgress.computed} / ${run.solveProgress.total} solves done`;

  return run.solveProgress.failed > 0
    ? `${done} · ${run.solveProgress.failed} failed`
    : done;
};

// The Run is Created the moment the upload response arrives (ADR-0004), so
// this step is always done by the time a Run exists to show progress for.
const uploadStep = (): StepperStep => ({
  key: 'uploaded',
  label: 'Uploaded',
  state: 'done',
});

const geocodingSub = (run: RunProgressRun): string => {
  const done = `${run.geocodedDeliveries} of ${run.totalDeliveries} done`;
  return run.failedGeocodes > 0
    ? `${done} · ${run.failedGeocodes} failed`
    : done;
};

/**
 * Geocoding only starts when a supervisor asks for it, so the step is upcoming
 * while rows still lack coordinates and nothing is pending.
 */
const geocodingStep = (run: RunProgressRun): StepperStep => {
  if (run.pendingGeocodes > 0) {
    return {
      key: 'geocoding',
      label: 'Geocoding',
      sub: geocodingSub(run),
      state: 'current',
    };
  }
  if (run.missingCoordinates > 0) {
    return {
      key: 'geocoding',
      label: 'Geocoding',
      sub: `${run.missingCoordinates} without coordinates`,
      state: 'upcoming',
    };
  }
  return { key: 'geocoding', label: 'Geocoding', state: 'done' };
};

const readyStep = (run: RunProgressRun): StepperStep => {
  const label = 'Ready to compute';
  if (run.status === CapacityPlanRunStatus.Created) {
    return {
      key: 'ready',
      label,
      sub:
        run.missingCoordinates > 0
          ? `${run.missingCoordinates} without coordinates excluded`
          : undefined,
      state: 'current',
    };
  }
  return { key: 'ready', label, state: 'done' };
};

const computedStep = (run: RunProgressRun): StepperStep => {
  const label = 'Computed';
  switch (run.status) {
    case CapacityPlanRunStatus.Queued:
    case CapacityPlanRunStatus.Computing:
      return {
        key: 'computed',
        label,
        sub: solveProgressLabelOf(run),
        state: 'current',
      };
    case CapacityPlanRunStatus.Computed:
      if (run.solveProgress.failed > 0) {
        return {
          key: 'computed',
          label,
          sub: `${run.solveProgress.failed} of ${run.solveProgress.total} solves failed`,
          state: 'warning',
        };
      }
      return {
        key: 'computed',
        label,
        sub: run.computedAt
          ? formatDateTime(new Date(run.computedAt))
          : undefined,
        state: 'done',
      };
    case CapacityPlanRunStatus.Failed:
      return { key: 'computed', label, state: 'failed' };
    default:
      return { key: 'computed', label, state: 'upcoming' };
  }
};

const nextActionOf = (
  run: RunProgressRun,
): Pick<RunProgress, 'nextAction' | 'blockedReason'> => {
  switch (run.status) {
    case CapacityPlanRunStatus.Created:
      // Rows without coordinates are skipped by the solve rather than holding
      // it back, so only a Run with nothing left to plan blocks the button.
      return run.validDeliveries - run.missingCoordinates > 0
        ? { nextAction: 'compute' }
        : {
            nextAction: 'none',
            blockedReason:
              'This run has no deliveries with coordinates to plan.',
          };
    case CapacityPlanRunStatus.Queued:
      return { nextAction: 'queued' };
    case CapacityPlanRunStatus.Computing:
      return { nextAction: 'computing' };
    case CapacityPlanRunStatus.Computed:
    case CapacityPlanRunStatus.Failed:
      return { nextAction: 'compute-again' };
    default:
      return { nextAction: 'none' };
  }
};

/**
 * Turns one Run into the four progress steps of the run header and the single
 * next action its primary button offers. Pure: counts in, view model out.
 */
export const deriveRunProgress = (run: RunProgressRun): RunProgress => ({
  steps: [uploadStep(), geocodingStep(run), readyStep(run), computedStep(run)],
  exclusionHint:
    run.missingCoordinates > 0
      ? `${run.missingCoordinates} rows without coordinates will be excluded`
      : undefined,
  solveProgressLabel: isRunSolving(run.status)
    ? solveProgressLabelOf(run)
    : undefined,
  ...nextActionOf(run),
});

/** The note shown on the Result tab. */
export const outOfCountryExcludedNote = (count: number) =>
  count === 1
    ? '1 delivery outside the country was excluded'
    : `${count} deliveries outside the country were excluded`;
